import { createElement, CSSProperties, useState } from 'react';
import { postfixTestId, TestIdProps } from '../../util/testId';
import { FontStyle, renderInlineCssVars, TextColor, useDesignTokens } from '../../theme';
import { assertNever } from '@sas/nova-core/typeguards';
import { DisabledDayConfig, DisabledDayFilter } from '../Calendar/DisabledDayFilters';
import { DatePickerProps } from '../DatePicker';
import { useDefaultDateTimeFormat } from '../DatePicker/useDefaultDateTimeFormat';
import { BaseSliderProps } from './__internal__';
import { BaseSlider } from './Slider';
import { SliderStepReducer, SliderStepReducerAction } from './SliderStep';
import { RangeSliderType, SliderType } from './SliderValues';
import { SliderTickLabels } from './Ticks';
import { SliderDatePicker } from './ValueGrip/SliderDatePicker';

import { useNCTextLocalizer } from '../../locale/useNCTextLocalizer';
import './DateSlider.css';
import cn from './DateSlider.css.json';

export type DateSliderDatePickerProps = {
  format?: null | ((value: Date) => string);
  disabled?: boolean;
  readOnly?: boolean;
  arrowGrips?: boolean;
} & (
  | ({ dateType?: null | 'date' } & {
      disabledDays?: DisabledDayConfig | DisabledDayFilter;
      showWeekNumbers?: boolean;
    })
  | ({ dateType: 'datetime' } & {
      disabledDays?: DisabledDayConfig | DisabledDayFilter;
      showWeekNumbers?: boolean;
      showMilliseconds?: boolean;
    })
  | { dateType: 'month' }
  | { dateType: 'quarter' }
  | { dateType: 'week' }
  | ({ dateType: 'time' } & {
      showMilliseconds?: boolean;
    })
);

export interface SingleDateSliderEvent {
  value: Date;
}

export interface SingleDateSliderProps {
  type?: 'value';
  value: Date;
  valueType?: SliderType;
  exclusive?: boolean;
  gripAriaText?: string;

  onChange: (event: SingleDateSliderEvent) => void;
  onCommit?: (event: SingleDateSliderEvent) => void;
}

export interface RangeDateSliderEvent {
  minValue: Date;
  maxValue: Date;
}

export interface RangeDateSliderProps {
  type: 'range';
  minValue: Date;
  maxValue: Date;
  valueType?: RangeSliderType;
  minExclusive?: boolean;
  maxExclusive?: boolean;
  minGripAriaText?: string;
  maxGripAriaText?: string;
  rangeAriaText?: string;

  onChange: (event: RangeDateSliderEvent) => void;
  onCommit?: (event: RangeDateSliderEvent) => void;
}

export type DateSliderStep = (previousValue: Date, action: SliderStepReducerAction) => Date;

export interface SharedDateSliderProps extends TestIdProps {
  /**
   * Unique identifier for the DateSlider. Used by "withLabels".
   */
  id?: string;
  className?: string;
  style?: CSSProperties;
  orientation?: 'horizontal' | 'vertical';
  scale: readonly [Date, Date];
  ticks?: Date[];
  hideInput?: boolean;
  hideValueLabel?: boolean;

  step?: null | DateSliderStep;
  stepMedium?: null | DateSliderStep;
  stepLarge?: null | DateSliderStep;

  handleLabelTokens?: {
    theme?: () => Partial<TextColor> | undefined;
    font?: Partial<FontStyle>;
  };
  tickLabelTokens?: SliderTickLabels['labelTokens'];
  ariaLabel?: string | undefined;
  ariaLabelledBy?: string | undefined;
}

export type DateSliderProps = (SingleDateSliderProps | RangeDateSliderProps) &
  DateSliderDatePickerProps &
  SharedDateSliderProps;

export const DateSlider = (props: DateSliderProps) => {
  const [sliderGripControlIsVisible, setSliderGripControlIsVisible] = useState(false);
  const localize = useNCTextLocalizer('Slider');
  let ariaLabels;
  let values: BaseSliderProps['values'];
  if (props.type === undefined || props.type === 'value') {
    values = [
      {
        type: 'value',
        value: props.value.getTime(),
        valueType: props.valueType,
        exclusive: props.exclusive,
        onChange({ value }) {
          props.onChange({ value: new Date(value) });
        },
        onCommit({ value }) {
          props.onCommit?.({ value: new Date(value) });
        },
      },
    ];
    ariaLabels = {
      gripAriaText: props.gripAriaText,
    };
  } else if (props.type === 'range') {
    values = [
      {
        type: 'range',
        minValue: props.minValue.getTime(),
        maxValue: props.maxValue.getTime(),
        valueType: props.valueType,
        minExclusive: props.minExclusive,
        maxExclusive: props.maxExclusive,
        onChange({ minValue, maxValue }) {
          props.onChange({
            minValue: new Date(minValue),
            maxValue: new Date(maxValue),
          });
        },
        onCommit({ minValue, maxValue }) {
          props.onCommit?.({
            minValue: new Date(minValue),
            maxValue: new Date(maxValue),
          });
        },
      },
    ];
    ariaLabels = {
      minGripAriaText: props.minGripAriaText,
      maxGripAriaText: props.maxGripAriaText,
      rangeAriaText: props.rangeAriaText,
    };
  } else {
    assertNever(props.type);
  }

  const adjustedScaleMin = adjustScaleValueForDate(props.scale[0]);
  const adjustedScaleMax = adjustScaleValueForDate(props.scale[1]);

  let datePickerProps: Partial<DatePickerProps>;
  if (props.dateType === undefined || props.dateType === 'date') {
    datePickerProps = {
      type: 'date',
      format: props.format,
      showWeekNumbers: props.showWeekNumbers,
      disabledDays: props.disabledDays,
      minDate: adjustedScaleMin,
      maxDate: adjustedScaleMax,
    };
  } else if (props.dateType === 'datetime') {
    datePickerProps = {
      type: 'datetime',
      format: props.format,
      showWeekNumbers: props.showWeekNumbers,
      disabledDays: props.disabledDays,
      minDate: adjustedScaleMin,
      maxDate: adjustedScaleMax,
      showMilliseconds: props.showMilliseconds,
    };
  } else if (props.dateType === 'time') {
    datePickerProps = {
      type: 'time',
      format: props.format,
      showMilliseconds: props.showMilliseconds,
    };
  } else if (
    props.dateType === 'month' ||
    props.dateType === 'quarter' ||
    props.dateType === 'week'
  ) {
    datePickerProps = {
      type: props.dateType,
      format: props.format,
      minDate: adjustedScaleMin,
      maxDate: adjustedScaleMax,
    };
  }

  const defaultFormat = useDefaultDateTimeFormat(props.dateType ?? 'date');

  const calculateDateSliderStep = (
    previousValue: Date,
    type: string,
    dateType: string | null | undefined
  ) => {
    const singleStep = type === 'forward' ? 1 : -1;
    const quarterStep = type === 'forward' ? 3 : -3;
    const decadeStep = type === 'forward' ? 10 : -10;
    const newValue = previousValue;
    let newDate = newValue.getDate();
    let newMonth: number;
    switch (dateType) {
      case 'datetime':
      case 'time':
        return new Date(previousValue.setSeconds(previousValue.getSeconds() + singleStep));
      case 'minute':
        return new Date(previousValue.setMinutes(previousValue.getMinutes() + singleStep));
      case 'hour':
        return new Date(previousValue.setHours(previousValue.getHours() + singleStep));
      case 'month':
        newMonth = previousValue.getMonth() + singleStep;
        switch (newMonth) {
          case 1:
            newDate = Math.min(newDate, 28);
            break;
          case 3:
          case 5:
          case 8:
          case 10:
            newDate = Math.min(newDate, 30);
            break;
        }
        newValue.setDate(newDate);
        newValue.setMonth(newMonth);
        return newValue;
      case 'quarter':
        newMonth = previousValue.getMonth() + quarterStep;
        if (newMonth === 1) {
          newDate = Math.min(newDate, 28);
        } else if (newMonth === 3 || newMonth === 5 || newMonth === 8 || newMonth === 10) {
          newDate = Math.min(newDate, 30);
        }
        newValue.setDate(newDate);
        newValue.setMonth(newMonth);
        return newValue;
      case 'year':
        return new Date(previousValue.setFullYear(previousValue.getFullYear() + singleStep));
      case 'decade':
        return new Date(previousValue.setFullYear(previousValue.getFullYear() + decadeStep));
      default:
        return new Date(previousValue.setDate(previousValue.getDate() + singleStep));
    }
  };
  const defaultDateSliderStep: DateSliderStep = (previousValue, { type }) => {
    return calculateDateSliderStep(previousValue, type, props.dateType);
  };
  const defaultMediumDateSliderStep: DateSliderStep = (previousValue, { type }) => {
    switch (props.dateType) {
      case 'datetime':
      case 'time':
        return calculateDateSliderStep(previousValue, type, 'minute');
      case 'month':
        return calculateDateSliderStep(previousValue, type, 'month');
      case 'quarter':
        return calculateDateSliderStep(previousValue, type, 'year');
      default:
        return calculateDateSliderStep(previousValue, type, 'month');
    }
  };
  const defaultLargeDateSliderStep: DateSliderStep = (previousValue, { type }) => {
    switch (props.dateType) {
      case 'datetime':
      case 'time':
        return calculateDateSliderStep(previousValue, type, 'hour');
      case 'month':
        return calculateDateSliderStep(previousValue, type, 'year');
      case 'quarter':
        return calculateDateSliderStep(previousValue, type, 'decade');
      default:
        return calculateDateSliderStep(previousValue, type, 'year');
    }
  };

  const dt = useDesignTokens();
  const tokens = renderInlineCssVars(
    {
      'spacing-s': dt.spacing.core.s,
      'spacing-l': dt.spacing.core.l,
    },
    'date-slider'
  );

  const inputAriaLabel =
    props.dateType === 'datetime'
      ? localize('slider.dateTimeInput.aria')
      : props.dateType === 'time'
      ? localize('slider.timeInput.aria')
      : localize('slider.dateInput.aria');
  return (
    <BaseSlider
      {...ariaLabels}
      sliderGripControlIsVisible={sliderGripControlIsVisible}
      className={props.className}
      disabled={props.disabled}
      readOnly={props.readOnly}
      arrowGrips={props.arrowGrips}
      hideInput={props.hideInput}
      hideValueLabel={props.hideValueLabel}
      style={{ ...tokens, ...props.style }}
      testId={props.testId}
      ariaLabel={props.ariaLabel}
      ariaLabelledBy={props.ariaLabelledBy}
      orientation={props.orientation}
      scale={[adjustedScaleMin.getTime(), adjustedScaleMax.getTime()]}
      gripLabelClassName={cn.compositeInputPadding[props.orientation ?? 'horizontal']}
      renderGripInput={(renderProps) => (
        <SliderDatePicker
          {...renderProps}
          testId={postfixTestId(renderProps.testId, 'datePicker')}
          ref={renderProps.ref}
          datePickerProps={datePickerProps}
          ariaLabel={inputAriaLabel}
          setSliderGripControlIsVisible={setSliderGripControlIsVisible}
        />
      )}
      format={{
        format(v) {
          return (props.format ?? defaultFormat)(new Date(v));
        },
        formatEditText() {
          throw new Error();
        },
        parse() {
          throw new Error();
        },
      }}
      values={values}
      handleLabelTokens={props.handleLabelTokens}
      tickLabelTokens={props.tickLabelTokens}
      ticks={props.ticks?.map((value) => adjustScaleValueForDate(value).getTime())}
      step={createSliderStep(props.step ?? defaultDateSliderStep)}
      stepMedium={createSliderStep(props.stepMedium ?? defaultMediumDateSliderStep)}
      stepLarge={createSliderStep(props.stepLarge ?? defaultLargeDateSliderStep)}
    />
  );
};

const createSliderStep = (dateSliderStep: DateSliderStep): SliderStepReducer => (
  previousValue,
  action
) => {
  const previousDate = new Date(previousValue);
  const nextDate = dateSliderStep(previousDate, action);
  return nextDate.getTime();
};

function adjustScaleValueForDate(date: Date) {
  const minDate = new Date('1/1/1582');
  const maxDate = new Date('12/31/9999');
  let adjustedDate = Math.max(date.getTime(), minDate.getTime());
  adjustedDate = Math.min(adjustedDate, maxDate.getTime());
  return new Date(adjustedDate);
}
